import React from "react";
import { styled } from "@linaria/react";
import { color } from "@ui";
import t from "./i18n";
import globalT from "../../i18n";
import Link from "@ui/Link";
import { analysisTitleLong } from "../Project/Analysis/analysisTitle";
import formatDate from "@ui/i18n/formatDate";
import { comparatorBy } from "diagnostics-lib/comparator";
import type { ActivityIndexJSON, ActivityType } from "../../api/useActivities";
import type { Project } from "../../api/useProjects";
import { useIndexedProjects } from "../../api/useProjects";
import { useDateRange } from "../Project/Analysis/useSelectedAnalyses";
import { uniq } from "diagnostics-lib/array";
import { useSelectedQuantityIds } from "../Project/useSelectedQuantities";
import { css } from "@linaria/core";
import Badge from "@ui/Badge";
import { InformationLine } from "@ui/IconV2/react";
import { useSetup } from "../../api/useSetups";
import { useTranslations, type Translation } from "../../api/useTranslations";
import { useProjectPeriod } from "../Project/useProjectPeriod";
import type { Source } from "../../api/useSources";
import { useSources } from "../../api/useSources";
import { useAlgorithmPresets } from "../../api/useAlgorithmPresets";
import { indexBy } from "diagnostics-lib/iterable";
import type { Reparability } from "../../api/Reparability";
import PatchSnapshotsSummary from "../Sources/PatchSnapshotsModalForm/Summary";
import { useRemoteFaultEvents } from "../../api/useFaultEvents";
import {
  getEventStatusText,
  type DenormalizedEventStatus,
} from "../Project/Events/utils/faultEventStatus";
import { getEventPriorityText } from "../Project/Events/utils/event";
import type { PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import { getDefaultFaultEventNames, getFaultEventStatus } from "diagnostics-domain/faultEvent";
import Text from "@ui/Text";
import { DEFAULT_CONFIG, Markdown } from "@ui/Markdown";
import { toPath } from "../../router/helpers/path";
import { convertFaultEventNaturalKeyToId } from "diagnostics-domain/event";

type Analysis = { label: string | null; group: string | null; date: string };

const AuthorNameWrapper = styled.span`
  color: ${color.carbon900};
  font-weight: 600;
`;

const StyledLink = styled(Link)`
  font-weight: 500;
  color: ${color.lightNitrogen500_Main};
`;

const LinkDisabled = styled.div`
  color: ${color.carbon800};
  text-decoration: line-through;
  display: inline-block;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;

const ListItemTitle = styled.div`
  font-weight: 600;
`;

const SublistItem = styled.li`
  margin-left: 16px;
`;

const SetupName = styled.span`
  color: ${color.carbon700};
  padding-right: 4px;
`;
const badgetWidth = css`
  display: inline-flex;
  width: fit-content;
`;

const Title = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const VerticalAlign = styled.span`
  * {
    vertical-align: text-bottom;
  }
`;

const inlineComments = css`
  display: inline-flex;
`;

const BadgeDeletedSetup = ({ setupName }: { setupName: string }) => (
  <VerticalAlign>
    <SetupName> {t("activity.badge.setup.setting") + " " + setupName}</SetupName>
    <Badge
      size="s"
      icon={<InformationLine color={color.darkNitrogen600} />}
      color={color.carbon900}
      backgroundColor={color.lightNitrogen50}
      className={badgetWidth}
    >
      {t("activity.badge.setup.deleted")}
    </Badge>
  </VerticalAlign>
);

const getSourceDuplicationRangeLabel = (
  from: string | null | undefined,
  to: string | null | undefined,
) => {
  if (!from && !to) return null;

  return t("activity.source_duplicated.range", {
    from: from ? formatDate(new Date(from), "datetime", null) : null,
    to: to ? formatDate(new Date(to), "datetime", null) : null,
  });
};

interface AcitivityDescription {
  title: React.ReactNode;
  projectId?: string | null;
  details?: () => React.ReactNode;
}

export function formatAuthorName(authorName: string | null) {
  return authorName ?? "Metroscope";
}

function AuthorName({ activity }: { activity: { userName: string } }) {
  return <AuthorNameWrapper>{activity.userName}</AuthorNameWrapper>;
}

function AnalysisList({
  analyses,
  projectId,
  title,
}: {
  projectId: string | null;
  analyses: { id: string; group: string | null; label: string | null; date: string }[];
  title?: string;
}) {
  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;
  const sortedAnalyses = [...analyses].sort(comparatorBy("date"));

  return (
    <>
      {title && <Title className="title">{title}</Title>}
      {sortedAnalyses.map(analysis => (
        <div key={analysis.id}>{analysisTitleLong(analysis, project?.timezone ?? null)}</div>
      ))}
      {sortedAnalyses.length === 0
        ? t("related_analyses_deleted", { count: analyses.length })
        : null}
    </>
  );
}

function SnapshotList({
  snapshots,
}: {
  snapshots: { group: string | null; label: string | null; date: string }[];
}) {
  const sortedSnapshots = [...snapshots].sort(comparatorBy("date"));

  return (
    <>
      {sortedSnapshots.map(snapshot => (
        <div key={snapshot.date}>{analysisTitleLong(snapshot, null)}</div>
      ))}
    </>
  );
}

function detailsFaultEventStatusChanged(
  properties: ActivityIndexJSON["FAULT_EVENT_STATUS_CHANGED"]["properties"],
  translation: Translation,
) {
  const previousStatus = getFaultEventStatus(
    {
      openedAt:
        properties.status.previous.openedAt === null
          ? null
          : new Date(properties.status.previous.openedAt),
      identifiedAction: properties.status.previous.identifiedAction || null,
      closedAt:
        properties.status.previous.closedAt === null
          ? null
          : new Date(properties.status.previous.closedAt),
    },
    [],
  );
  const currentStatus = getFaultEventStatus(
    {
      openedAt:
        properties.status.current.openedAt === null
          ? null
          : new Date(properties.status.current.openedAt),
      identifiedAction: properties.status.current.identifiedAction || null,
      closedAt:
        properties.status.current.closedAt === null
          ? null
          : new Date(properties.status.current.closedAt),
    },
    [],
  );

  const reparabilityChange =
    properties.reparability?.current !== null &&
    properties.reparability?.previous !== properties.reparability?.current ? (
      <Text type="body2">
        {t("fault_event_status_changed.reparability", {
          reparability: properties.reparability.current,
          translation,
        })}
      </Text>
    ) : null;

  const detectionChange =
    properties.detection?.current !== null &&
    properties.detection?.previous !== properties.detection?.current ? (
      <Text type="body2">
        {t("fault_event_status_changed.detection", {
          detection: properties.detection.current,
        })}
      </Text>
    ) : null;

  const actionChange =
    properties.action?.current !== null &&
    properties.action?.previous !== properties.action?.current ? (
      <Markdown
        config={{
          ...DEFAULT_CONFIG,
          theme: {
            ...DEFAULT_CONFIG.theme,
            paragraph: "paragraph-body2",
          },
        }}
        defaultValue={t("fault_event_status_changed.action", {
          action: properties.action.current,
        })}
      />
    ) : null;

  return (
    <>
      <Text type="body2">
        {t("fault_event_status_changed.status", {
          previous: previousStatus,
          current: currentStatus,
        })}
      </Text>
      {reparabilityChange}
      {detectionChange}
      {actionChange}
    </>
  );
}

function detailsProjectChanged(
  { previous, current }: ActivityIndexJSON["PROJECT_CHANGED"]["properties"],
  translation: Translation,
) {
  const previousName = previous.name;
  const currentName = current.name;

  const currentDraft = current.draft;

  const previousTimezone = previous.timezone;
  const currentTimezone = current.timezone;

  const previousDescription = previous.description;
  const currentDescription = current.description;

  const previousTypeName = previous.typeName;
  const currentTypeName = current.typeName;

  const previousSiteName = previous.siteName;
  const currentSiteName = current.siteName;

  const previousSourceId = previous.sourceId;
  const currentSourceId = current.sourceId;

  return (
    <>
      {previousSourceId === currentSourceId ? null : (
        <p>
          {t("project_changed.details.sourceId", {
            previous: previousSourceId!,
            current: currentSourceId!,
          })}
        </p>
      )}
      {currentName !== undefined && previousName !== undefined && currentName !== previousName && (
        <p>{t("project_changed.details.name", { previousName, currentName })}</p>
      )}

      {currentDraft !== undefined &&
        (currentDraft ? (
          <p>{t("project_changed.details.production.draft")}</p>
        ) : (
          <p>{t("project_changed.details.production")}</p>
        ))}

      {currentTimezone !== undefined &&
        previousTimezone !== undefined &&
        currentTimezone !== previousTimezone && (
          <p>
            {t("project_changed.details.timezone", {
              previousTimezone,
              currentTimezone,
            })}
          </p>
        )}

      {currentDescription && previousDescription === null && (
        <p>{t("project_changed.details.description.add", { currentDescription })}</p>
      )}

      {currentDescription === null && previousDescription && (
        <p>{t("project_changed.details.description.deleted")}</p>
      )}

      {currentDescription && previousDescription && previousDescription !== currentDescription && (
        <p>
          {t("project_changed.details.description.modified", {
            previousDescription,
            currentDescription,
          })}
        </p>
      )}

      {currentTypeName && previousTypeName === null && (
        <p>{t("project_changed.details.type.add", { currentTypeName, translation })}</p>
      )}

      {currentTypeName === null && previousTypeName && (
        <p>{t("project_changed.details.type.deleted", { translation })}</p>
      )}

      {currentTypeName && previousTypeName && previousTypeName !== currentTypeName && (
        <p>
          {t("project_changed.details.type.modified", {
            previousTypeName,
            currentTypeName,
            translation,
          })}
        </p>
      )}

      {currentSiteName && previousSiteName === null && (
        <p>{t("project_changed.details.site.add", { currentSiteName })}</p>
      )}

      {currentSiteName === null && previousSiteName && (
        <p>{t("project_changed.details.site.deleted")}</p>
      )}

      {currentSiteName && previousSiteName && previousSiteName !== currentSiteName && (
        <p>
          {t("project_changed.details.site.modified", {
            previousSiteName,
            currentSiteName,
          })}
        </p>
      )}
    </>
  );
}

function detailsQuantitiesChanged(
  property: ActivityIndexJSON["QUANTITIES_CHANGED"]["properties"][number],
  timeZone: string | null,
) {
  const { previous, current, formulaVersions } = property;
  const previousName = previous.name;
  const currentName = current.name;
  const previousDescription = previous.description;
  const currentDescription = current.description;
  const previousUnit = previous.unit;
  const currentUnit = current.unit;
  const previousFormula = previous.formula;
  const currentFormula = current.formula;
  const previousMin = previous.min;
  const currentMin = current.min;
  const currentMax = current.max;
  const previousMax = previous.max;
  const currentMaxstd = current.maxStd;
  const previousMaxstd = previous.maxStd;

  const currentSystemName = current.systemName;
  const previousSystemName = previous.systemName;
  const currentEquipmentTypeName = current.equipmentTypeName;
  const previousEquipmentTypeName = previous.equipmentTypeName;
  const currentEquipments = current.equipments;
  const previousEquipments = previous.equipments;

  return (
    <>
      {currentName !== undefined && previousName !== undefined && currentName !== previousName && (
        <p>{t("quantites_changed.details.name.modified", { previousName, currentName })}</p>
      )}
      {currentFormula !== undefined && previousFormula !== undefined && (
        <p>
          {t("quantites_changed.details.formula", {
            previousFormula,
            currentFormula,
          })}
        </p>
      )}
      {currentUnit !== undefined && previousUnit !== undefined && (
        <p>
          {t("quantites_changed.details.unit", {
            previousUnit,
            currentUnit,
          })}
        </p>
      )}
      {currentDescription !== undefined && previousDescription !== undefined && (
        <p>
          {t("quantites_changed.details.description", {
            previousDescription,
            currentDescription,
          })}
        </p>
      )}
      {currentMin !== undefined && previousMin !== undefined && (
        <p>
          {t("quantites_changed.details.min", {
            previousMin,
            currentMin,
          })}
        </p>
      )}
      {currentMax !== undefined && previousMax !== undefined && (
        <p>
          {t("quantites_changed.details.max", {
            previousMax,
            currentMax,
          })}
        </p>
      )}
      {currentMaxstd !== undefined && previousMaxstd !== undefined && (
        <p>
          {t("quantites_changed.details.maxstd", {
            previousMaxstd,
            currentMaxstd,
          })}
        </p>
      )}

      {currentSystemName !== undefined && previousSystemName !== undefined && (
        <p>
          {t("quantites_changed.details.system", {
            previousSystemName,
            currentSystemName,
          })}
        </p>
      )}
      {currentEquipmentTypeName !== undefined && previousEquipmentTypeName !== undefined && (
        <p>
          {t("quantites_changed.details.equipmentType", {
            previousEquipmentTypeName,
            currentEquipmentTypeName,
          })}
        </p>
      )}
      {currentEquipments !== undefined && previousEquipments !== undefined && (
        <p>
          {t("quantites_changed.details.equipments", {
            previousEquipments,
            currentEquipments,
          })}
        </p>
      )}
      {formulaVersions?.map(({ action, startsAt, expression }) => {
        const formattedStartsAt = formatDate(new Date(startsAt), "date", timeZone);
        const message =
          action === "CREATED"
            ? t("quantites_changed.details.formulaVersion.created", {
                startsAt: formattedStartsAt,
                expression,
              })
            : action === "UPDATED"
              ? t("quantites_changed.details.formulaVersion.updated", {
                  startsAt: formattedStartsAt,
                  expression,
                })
              : t("quantites_changed.details.formulaVersion.deleted", {
                  startsAt: formattedStartsAt,
                  expression,
                });

        return <p key={`${action}-${startsAt}-${expression}`}>{message}</p>;
      })}
    </>
  );
}

function detailsSetupDiagnosisFailuresMetadataChanged(
  {
    previous,
    current,
  }: ActivityIndexJSON["SETUP_DIAGNOSIS_FAILURES_METADATA_CHANGED"]["properties"],
  translation: Translation,
) {
  const previousDisplayName = previous.displayName;
  const currentDisplayName = current.displayName;
  const previousDescription = previous.description;
  const currentDescription = current.description;
  const previousUnit = previous.unit;
  const currentUnit = current.unit;
  const currentSystemName = current.systemName;
  const previousSystemName = previous.systemName;
  const currentEquipmentTypeName = current.equipmentTypeName;
  const previousEquipmentTypeName = previous.equipmentTypeName;
  const currentDegradationTypeName = current.degradationTypeName;
  const previousDegradationTypeName = previous.degradationTypeName;
  const currentFailureCategoryName = current.failureCategoryName;
  const previousFailureCategoryName = previous.failureCategoryName;
  const currentReparability = current.reparability;
  const previousReparability = previous.reparability;
  const currentEquipments = current.equipments;
  const previousEquipments = previous.equipments;
  const currentSuspectedElements = current.suspectedElements;
  const previousSuspectedElements = previous.suspectedElements;

  return (
    <>
      {currentDisplayName !== undefined &&
        currentDisplayName !== null &&
        previousDisplayName !== undefined &&
        previousDisplayName !== null && (
          <p>
            {t("setupDiagnosisFailuresMetatada_changed.details.displayName", {
              previousDisplayName,
              currentDisplayName,
            })}
          </p>
        )}
      {currentUnit !== undefined && previousUnit !== undefined && (
        <p>
          {t("setupDiagnosisFailuresMetatada_changed.details.unit", {
            previousUnit,
            currentUnit,
          })}
        </p>
      )}
      {currentDescription !== undefined && previousDescription !== undefined && (
        <p>
          {t("setupDiagnosisFailuresMetatada_changed.details.description", {
            previousDescription,
            currentDescription,
          })}
        </p>
      )}
      {currentSystemName !== undefined && previousSystemName !== undefined && (
        <p>
          {t("setupDiagnosisFailuresMetatada_changed.details.system", {
            previousSystemName,
            currentSystemName,
          })}
        </p>
      )}
      {currentEquipmentTypeName !== undefined && previousEquipmentTypeName !== undefined && (
        <p>
          {t("setupDiagnosisFailuresMetatada_changed.details.equipmentType", {
            previousEquipmentTypeName,
            currentEquipmentTypeName,
          })}
        </p>
      )}
      {currentDegradationTypeName !== undefined && previousDegradationTypeName !== undefined && (
        <p>
          {t("setupDiagnosisFailuresMetatada_changed.details.degradationType", {
            previousDegradationTypeName,
            currentDegradationTypeName,
          })}
        </p>
      )}
      {currentFailureCategoryName !== undefined && previousFailureCategoryName !== undefined && (
        <p>
          {t("setupDiagnosisFailuresMetatada_changed.details.failureCategory", {
            previousFailureCategoryName,
            currentFailureCategoryName,
          })}
        </p>
      )}
      {currentReparability !== undefined && previousReparability !== undefined && (
        <p>
          {t("setupDiagnosisFailuresMetatada_changed.details.reparability", {
            previousReparability,
            currentReparability,
            translation,
          })}
        </p>
      )}
      {currentEquipments !== undefined && previousEquipments !== undefined && (
        <p>
          {t("setupDiagnosisFailuresMetatada_changed.details.equipments", {
            previousEquipments,
            currentEquipments,
          })}
        </p>
      )}
      {currentSuspectedElements !== undefined && previousSuspectedElements !== undefined && (
        <p>
          {t("setupDiagnosisFailuresMetatada_changed.details.suspectedElements", {
            previousSuspectedElements,
            currentSuspectedElements,
          })}
        </p>
      )}
    </>
  );
}

function CommentCreatedDetails({
  activityData: { analysis, projectId, commentText, faultEvent, dashboardName },
}: {
  activityData: {
    commentText: string;
    projectId: string | null;
    analysis: { label: string | null; group: string | null; date: string } | null;
    faultEvent: {
      id: string | null;
      projectId: string | null;
      failureName: string | null;
      start: string | null;
    } | null;
    dashboardName: string | null;
  };
}) {
  const translations = useTranslations();

  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;
  const faultEvents = useRemoteFaultEvents(projectId ?? "");
  const legacyFaultEventId =
    faultEvent !== null &&
    faultEvent.projectId !== null &&
    faultEvent.failureName !== null &&
    faultEvent.start !== null
      ? convertFaultEventNaturalKeyToId(
          faultEvent.projectId,
          faultEvent.failureName,
          new Date(faultEvent.start),
        )
      : null;
  const matchingFaultEvent = faultEvents.find(
    f => f.id === faultEvent?.id || f.id === legacyFaultEventId,
  );

  const faultEventName =
    matchingFaultEvent?.name === null
      ? faultEvent !== null && faultEvent.failureName !== null && faultEvent.start !== null
        ? getDefaultFaultEventNames([faultEvent.failureName], new Date(faultEvent.start))
        : null
      : (matchingFaultEvent?.name ?? null);

  return (
    <>
      {analysis || faultEventName || dashboardName ? (
        <>
          {t("comment_linked", {
            analysisDate: analysis ? analysisTitleLong(analysis, project?.timezone ?? null) : null,
            faultEventName,
            dashboardName,
            translations,
          })}
        </>
      ) : (
        t("comment")
      )}
      &quot;
      <div className={inlineComments}>
        <Markdown defaultValue={commentText} />
        &quot;
      </div>
    </>
  );
}

function CommentEditedDetails({
  activityData: { analysis, previousCommentText, currentCommentText, projectId, faultEvent },
}: {
  activityData: {
    previousCommentText: string;
    currentCommentText: string;
    projectId: string | null;
    analysis: Analysis | null;
    faultEvent: {
      projectId: string;
      failureNames: string[];
      start: string;
    } | null;
  };
}) {
  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;
  const faultEvents = useRemoteFaultEvents(projectId ?? "");
  const matchingFaultEvent = faultEvents.find(
    f =>
      projectId === faultEvent?.projectId &&
      f.faultEventFailures.length === faultEvent.failureNames.length &&
      f.faultEventFailures.every(({ failureName }) =>
        faultEvent.failureNames.includes(failureName),
      ) &&
      f.startsAt === faultEvent.start,
  );

  const faultEventName =
    matchingFaultEvent?.name === null
      ? faultEvent
        ? getDefaultFaultEventNames(faultEvent.failureNames, new Date(faultEvent.start))
        : null
      : (matchingFaultEvent?.name ?? null);

  return (
    <>
      {analysis || faultEventName ? (
        <>
          {t("comment_edited.linked", {
            analysisDate: analysis ? analysisTitleLong(analysis, project?.timezone ?? null) : null,
            faultEventName,
          })}
        </>
      ) : (
        t("comment")
      )}

      <div>
        <div className={inlineComments}>
          {t("comment_new")}&quot;
          <Markdown defaultValue={currentCommentText} />
          &quot;
        </div>
      </div>

      <div>
        <div className={inlineComments}>
          {t("comment_old")}&quot;
          <Markdown defaultValue={previousCommentText} />
          &quot;
        </div>
      </div>
    </>
  );
}

function MesuresValidityChangedTitle({
  authorName,
  action,
  projectId,
  sortedAnalyses,
}: {
  authorName: React.ReactNode;
  action: "VALIDATION" | "INVALIDATION";
  projectId: string | null;
  sortedAnalyses: ActivityIndexJSON["ANALYSIS_MEASURES_VALIDITY_CHANGED"]["properties"]["analyses"];
}) {
  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;
  const source = useSources().find(s => s.id === project?.sourceId);

  const firstAnalysis = sortedAnalyses[0];
  const lastAnalysis = sortedAnalyses[sortedAnalyses.length - 1];
  if (!firstAnalysis || !lastAnalysis) throw new Error("Missing analyses");
  const count = sortedAnalyses.reduce((sum, a) => sum + a.quantityNames.length, 0);

  const timezone = project ? project.timezone : null;
  const info = {
    firstAnalysis,
    lastAnalysis,
    count,
  };

  const link =
    project && source ? (
      <MeasureLink project={project} source={source} sortedAnalyses={sortedAnalyses} info={info} />
    ) : (
      <LinkDisabled> {t("measure", { count })}</LinkDisabled>
    );

  return (
    <>
      {t("measures_validity_changed.title", {
        authorName,
        action,
        measuresLink: link,
        firstDate: formatDate(firstAnalysis.date, "date", timezone),
        lastDate: formatDate(lastAnalysis.date, "date", timezone),
      })}
    </>
  );
}

function MeasureLink({
  project,
  source,
  sortedAnalyses,
  info,
}: {
  project: Project;
  source: Source;
  sortedAnalyses: ActivityIndexJSON["ANALYSIS_MEASURES_VALIDITY_CHANGED"]["properties"]["analyses"];
  info: {
    firstAnalysis: ActivityIndexJSON["ANALYSIS_MEASURES_VALIDITY_CHANGED"]["properties"]["analyses"][number];
    lastAnalysis: ActivityIndexJSON["ANALYSIS_MEASURES_VALIDITY_CHANGED"]["properties"]["analyses"][number];
    count: number;
  };
}) {
  const [_dateRange, setDateRange] = useDateRange({
    scope: project.id,
  });
  const [_selectedQuantityIds, setSelectedQuantityIds] = useSelectedQuantityIds({
    scope: project.id,
  });
  const [, , , setPeriodCenteredOnDates] = useProjectPeriod({
    source,
    project,
    boundaries: "analyses",
  });

  return (
    <StyledLink
      to={toPath("/projects/:projectId/measures", {
        projectId: project.id,
      })}
      onClick={() => {
        setSelectedQuantityIds(
          uniq(sortedAnalyses.flatMap(a => a.quantityIds)).map((id, index) => ({
            id,
            colorIndex: index as 0,
          })),
        );
        setDateRange({
          pivot: new Date(info.firstAnalysis.date).getTime(),
          to: new Date(info.lastAnalysis.date).getTime(),
        });
        setPeriodCenteredOnDates({
          pivot: new Date(info.firstAnalysis.date).getTime(),
          to: new Date(info.lastAnalysis.date).getTime(),
        });
      }}
    >
      {t("measure", { count: info.count })}
    </StyledLink>
  );
}

function FailureStatusChangedTitle({
  authorName,
  status,
  projectId,
  sortedAnalyses,
}: {
  authorName: React.ReactNode;
  status: ActivityIndexJSON["FAILURE_STATUS_CHANGED"]["properties"]["status"];
  projectId: string | null;
  sortedAnalyses: ActivityIndexJSON["FAILURE_STATUS_CHANGED"]["properties"]["analyses"];
}) {
  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;
  const timezone = project ? project.timezone : null;

  const startAnalysis = sortedAnalyses[0];
  const lastAnalysis = sortedAnalyses[sortedAnalyses.length - 1];
  if (!startAnalysis || !lastAnalysis) throw new Error("Missing analyses");
  const startDate = formatDate(startAnalysis.date, "date", timezone);
  const endDate = formatDate(lastAnalysis.date, "date", timezone);

  const count = sortedAnalyses.reduce((sum, a) => sum + a.failureNames.length, 0);
  const failureLink = projectId ? (
    <StyledLink
      to={toPath("/projects/:projectId/diagnosis", {
        projectId,
      })}
    >
      {t("failure", { count })}
    </StyledLink>
  ) : (
    <LinkDisabled> {t("failure", { count })}</LinkDisabled>
  );
  return (
    <>
      {t("failure_status_changed.title", {
        authorName,
        failureLink,
        status,
        startDate,
        endDate,
      })}
    </>
  );
}

function FailureReintegratedTitle({
  authorName,
  projectId,
  sortedAnalyses,
}: {
  authorName: React.ReactNode;
  projectId: string | null;
  sortedAnalyses: ActivityIndexJSON["FAILURE_STATUS_CHANGED"]["properties"]["analyses"];
}) {
  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;
  const timezone = project ? project.timezone : null;

  const startAnalysis = sortedAnalyses[0];
  const lastAnalysis = sortedAnalyses[sortedAnalyses.length - 1];
  if (!startAnalysis || !lastAnalysis) throw new Error("Missing analyses");
  const startDate = formatDate(startAnalysis.date, "date", timezone);
  const endDate = formatDate(lastAnalysis.date, "date", timezone);

  const count = sortedAnalyses.reduce((sum, a) => sum + a.failureNames.length, 0);
  const failureLink = projectId ? (
    <StyledLink
      to={toPath("/projects/:projectId/diagnosis", {
        projectId,
      })}
    >
      {t("failure", { count })}
    </StyledLink>
  ) : (
    <LinkDisabled> {t("failure", { count })}</LinkDisabled>
  );
  return (
    <>
      {t("fault_reintegrated.title", {
        authorName,
        failureLink,
        startDate,
        endDate,
      })}
    </>
  );
}

function FailureReparabilityChangedTitle({
  authorName,
  reparability,
  projectId,
  sortedAnalyses,
  translation,
}: {
  authorName: React.ReactNode;
  reparability: Reparability | null;
  projectId: string | null;
  sortedAnalyses: ActivityIndexJSON["FAILURE_REPARABILITY_CHANGED"]["properties"]["analyses"];
  translation: Translation;
}) {
  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;
  const timezone = project ? project.timezone : null;

  const startAnalysis = sortedAnalyses[0];
  const lastAnalysis = sortedAnalyses[sortedAnalyses.length - 1];
  if (!startAnalysis || !lastAnalysis) throw new Error("Missing analyses");
  const startDate = formatDate(startAnalysis.date, "date", timezone);
  const endDate = formatDate(lastAnalysis.date, "date", timezone);

  const count = sortedAnalyses.reduce((sum, a) => sum + a.failureNames.length, 0);
  const failureLink = projectId ? (
    <StyledLink
      to={toPath("/projects/:projectId/diagnosis", {
        projectId,
      })}
    >
      {t("failure", { count })}
    </StyledLink>
  ) : (
    <LinkDisabled> {t("failure", { count })}</LinkDisabled>
  );
  return (
    <>
      {t("failure_reparability_changed.title", {
        authorName,
        failureLink,
        reparability,
        startDate,
        endDate,
        translation,
      })}
    </>
  );
}

function AnalysisActivatedDetails({
  sortedAnalysisActivated,
  projectId,
}: {
  projectId: string | null;
  sortedAnalysisActivated: ActivityIndexJSON["ANALYSIS_ACTIVATED"]["properties"]["analyses"];
}) {
  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;

  return (
    <List>
      {sortedAnalysisActivated.map(analysis => (
        <li key={analysis.id}>
          <SublistItem>
            {analysisTitleLong(analysis, project ? project.timezone : null)}
          </SublistItem>
        </li>
      ))}
    </List>
  );
}

function DashboardLink(
  projectId: string | null,
  dashboardId: string | null,
  dashboardName: string,
) {
  if (projectId && dashboardId) {
    return (
      <StyledLink
        to={toPath("/projects/:projectId/dashboards/:dashboardId", {
          projectId,
          dashboardId,
        })}
      >
        {dashboardName}
      </StyledLink>
    );
  } else {
    return <LinkDisabled>{dashboardName}</LinkDisabled>;
  }
}

function FaultEventLink(projectId: string | null, faultEventId: string | null, name: string) {
  const faultEvents = useRemoteFaultEvents(projectId ?? "");
  const matchingFaultEvent = faultEvents.find(f => f.id === faultEventId);

  if (!projectId || faultEventId === null || matchingFaultEvent === undefined)
    return <LinkDisabled>{name}</LinkDisabled>;

  return (
    <StyledLink
      to={toPath("/projects/:projectId/fault-events/:faultEventId/fault", {
        projectId,
        faultEventId,
      })}
    >
      {matchingFaultEvent?.name ?? name}
    </StyledLink>
  );
}

function ManualFaultEventLink(
  projectId: string | null,
  manualFaultEventId: string | null,
  name: string,
) {
  if (!projectId || manualFaultEventId === null) return <LinkDisabled>{name}</LinkDisabled>;

  return (
    <StyledLink
      to={toPath("/projects/:projectId/fault-events/manual/:manualFaultEventId/fault", {
        projectId,
        manualFaultEventId,
      })}
    >
      {name}
    </StyledLink>
  );
}

function formatManualFaultEventTimeWindow(startDate: string, endDate: string | null) {
  return endDate ? `${startDate} - ${endDate}` : `${startDate}`;
}

function AnalysisDeactivated({
  sortedAnalysisDeactivated,
  projectId,
}: {
  projectId: string | null;
  sortedAnalysisDeactivated: ActivityIndexJSON["ANALYSIS_DEACTIVATED"]["properties"]["analyses"];
}) {
  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;

  return (
    <List>
      {sortedAnalysisDeactivated.map(analysis => (
        <li key={analysis.id}>
          <SublistItem>
            {analysisTitleLong(analysis, project ? project.timezone : null)}
          </SublistItem>
        </li>
      ))}
    </List>
  );
}

function AnalysisMeasuresValidityChangedDetails({
  sortedAnalysisMeasuresValidityChangedDetails,
  projectId,
}: {
  projectId: string | null;
  sortedAnalysisMeasuresValidityChangedDetails: ActivityIndexJSON["ANALYSIS_MEASURES_VALIDITY_CHANGED"]["properties"]["analyses"];
}) {
  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;

  return (
    <List>
      {sortedAnalysisMeasuresValidityChangedDetails.map(analysis => {
        const quantityNames = [...analysis.quantityNames].sort();
        return (
          <li key={analysis.id}>
            <ListItemTitle>
              {analysisTitleLong(analysis, project ? project.timezone : null)}
            </ListItemTitle>
            <ul>
              {quantityNames.map(quantityName => (
                <SublistItem key={quantityName}>{quantityName}</SublistItem>
              ))}
            </ul>
          </li>
        );
      })}
    </List>
  );
}

function FailureStatusChangedDetails({
  sortedFailureStatusChanged,
  projectId,
}: {
  projectId: string | null;
  sortedFailureStatusChanged: ActivityIndexJSON["FAILURE_STATUS_CHANGED"]["properties"]["analyses"];
}) {
  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;

  return (
    <List>
      {sortedFailureStatusChanged.map(analysis => {
        const sortedFailureNames = [...analysis.failureNames].sort();
        return (
          <li key={analysis.id}>
            <ListItemTitle>
              {analysisTitleLong(analysis, project ? project.timezone : null)}
            </ListItemTitle>
            <ul>
              {sortedFailureNames.map(failureName => (
                <SublistItem key={failureName}>{failureName}</SublistItem>
              ))}
            </ul>
          </li>
        );
      })}
    </List>
  );
}

function FailureReparabilityChangedDetails({
  sortedFailureReparabilityChanged,
  projectId,
}: {
  projectId: string | null;
  sortedFailureReparabilityChanged: ActivityIndexJSON["FAILURE_REPARABILITY_CHANGED"]["properties"]["analyses"];
}) {
  const projects = useIndexedProjects();
  const project = projectId ? (projects[projectId] ?? null) : null;

  return (
    <List>
      {sortedFailureReparabilityChanged.map(analysis => {
        const sortedFailureNames = [...analysis.failureNames].sort();
        return (
          <li key={analysis.id}>
            <ListItemTitle>
              {analysisTitleLong(analysis, project ? project.timezone : null)}
            </ListItemTitle>
            <ul>
              {sortedFailureNames.map(failureName => (
                <SublistItem key={failureName}>{failureName}</SublistItem>
              ))}
            </ul>
          </li>
        );
      })}
    </List>
  );
}

function ProjectDuplicatedTitle({
  authorName,
  targetProject,
}: {
  authorName: React.ReactNode;
  targetProject: { id: string; name: string };
}) {
  const projects = useIndexedProjects();
  const project = projects[targetProject.id] ?? null;
  const targetProjectFound = project !== null;
  const isTargetProjectExist = targetProjectFound !== undefined;
  const targetProjectLink = isTargetProjectExist ? (
    <StyledLink
      to={toPath("/projects/:projectId/measures", {
        projectId: targetProject.id,
      })}
    >
      {targetProject.name}
    </StyledLink>
  ) : (
    <LinkDisabled>{targetProject.name}</LinkDisabled>
  );
  return (
    <>
      {t("activity.project_duplicated.title", {
        authorName,
        targetProjectLink,
      })}
    </>
  );
}

function ProjectDuplicatedFromTitle({
  authorName,
  sourceProjet,
}: {
  authorName: React.ReactNode;
  sourceProjet: { id: string; name: string };
}) {
  const projects = useIndexedProjects();
  const sourceProjectFound = projects[sourceProjet.id] !== undefined;
  const sourceProjectLink = sourceProjectFound ? (
    <StyledLink
      to={toPath("/projects/:projectId/measures", {
        projectId: sourceProjet.id,
      })}
    >
      {sourceProjet.name}
    </StyledLink>
  ) : (
    <LinkDisabled>{sourceProjet.name}</LinkDisabled>
  );
  return (
    <>
      {t("activity.project_duplicated_from.title", {
        authorName,
        sourceProjectLink,
      })}
    </>
  );
}

function SetupDuplicatedTitle({
  projectId,
  setupId,
  setupName,
}: {
  projectId: string;
  setupId: string;
  setupName: string;
}) {
  const setup = useSetup({ projectId, setupId });

  return projectId && setup ? (
    <>
      {t("activity.badge.setup.setting")}{" "}
      <StyledLink
        to={toPath("/projects/:projectId/setup/:setupId", {
          projectId,
          setupId,
        })}
      >
        {setup.name}
      </StyledLink>
    </>
  ) : (
    <LinkDisabled>{setupName}</LinkDisabled>
  );
}

function SetupPresetChangedTitle({
  activity,
}: {
  activity: ActivityIndexJSON["SETUP_DIAGNOSIS_PRESET_CHANGED"];
}) {
  const algorihtmePresets = useAlgorithmPresets();
  const indexedByPresetKey = indexBy(algorihtmePresets, "key");

  const prevPresetKey =
    indexedByPresetKey.get(activity.properties.previous.presetKey)?.translateKey ??
    activity.properties.previous.presetKey;
  const currPresetKey =
    indexedByPresetKey.get(activity.properties.current.presetKey)?.translateKey ??
    activity.properties.current.presetKey;

  return (
    <>
      {t("setup_diagnosis_preset_changed.title", {
        authorName: <AuthorName activity={activity} />,
        setupLink:
          activity.projectId && activity.setupId ? (
            <>
              {t("activity.badge.setup.setting")}{" "}
              <StyledLink
                to={
                  toPath("/projects/:projectId/setup/:setupId", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  }) + "#algorithm"
                }
              >
                {activity.setupName}
              </StyledLink>
            </>
          ) : (
            <BadgeDeletedSetup setupName={activity.setupName} />
          ),
        prevPreset: prevPresetKey,
        currPreset: currPresetKey,
      })}
    </>
  );
}

function SetupParametersChangedTitle({
  activity,
}: {
  activity: ActivityIndexJSON["SETUP_DIAGNOSIS_PARAMETERS_CHANGED"];
}) {
  const algorihtmePresets = useAlgorithmPresets();
  const indexedByPresetKey = indexBy(algorihtmePresets, "key");

  const preset = indexedByPresetKey.get(activity.properties.presetKey);

  return (
    <>
      {t("setup_diagnosis_parameters_changed.title", {
        authorName: <AuthorName activity={activity} />,
        setupLink:
          activity.projectId && activity.setupId ? (
            <>
              {t("activity.badge.setup.setting")}{" "}
              <StyledLink
                to={
                  toPath("/projects/:projectId/setup/:setupId", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  }) + "#algorithm"
                }
              >
                {activity.setupName}
              </StyledLink>
            </>
          ) : (
            <BadgeDeletedSetup setupName={activity.setupName} />
          ),
        presetName: preset?.translateKey ?? activity.properties.presetKey,
      })}
    </>
  );
}

function SetupParametersChangedDetails({
  properties,
}: {
  properties: ActivityIndexJSON["SETUP_DIAGNOSIS_PARAMETERS_CHANGED"]["properties"];
}) {
  const algorihtmePresets = useAlgorithmPresets();
  const indexedByPresetKey = indexBy(algorihtmePresets, "key");

  const preset = indexedByPresetKey.get(properties.presetKey);
  const indexedByParameters = indexBy(preset?.parameters ?? [], "key");

  const prevParameters = properties.previous.parameters.map(p => {
    const prevParameter = indexedByParameters.get(p.key);
    return {
      key: prevParameter?.translateKey ?? p.key,
      value: p.value,
    };
  });

  const currParameters = properties.current.parameters.map(p => {
    const currParameter = indexedByParameters.get(p.key);
    return {
      key: currParameter?.translateKey ?? p.key,
      value: p.value,
    };
  });

  return (
    <>
      {t("activity.setup_diagnosis_algorithm_changed.details", {
        prevParameters,
        currParameters,
      })}
    </>
  );
}

// needed for discriminating in the switch case below :(((
type DiscriminableActivity = {
  [T in ActivityType]: {
    type: T;
  } & ActivityIndexJSON[T];
}[ActivityType];

function getActivityDescription(
  activity: DiscriminableActivity,
  translation: Translation,
  projectTimeZone: string | null,
): AcitivityDescription {
  // Use the activityLabels
  const activityLabels = {
    ANALYSIS_ACTIVATION_FILTERS_DELETED: t("selectFilter.analysis_activation_filters_deleted"),
    ANALYSIS_ACTIVATION_FILTERS_REPLACED: t("selectFilter.analysis_activation_filters_replaced"),
    ANALYSIS_ACTIVATION_FREQUENCY_CHANGED: t("selectFilter.analysis_activation_frenquency_changed"),
    ANALYSIS_ACTIVATION_RESET: t("selectFilter.analysis_activation_reset"),
    ANALYSIS_ACTIVATED: t("selectFilter.analysis_activated"),
    ANALYSIS_DEACTIVATED: t("selectFilter.analysis_deactivated"),
    ANALYSIS_PUBLICATION_CHANGED: t("analysis_publication_changed"),
    ANALYSIS_MEASURES_VALIDITY_CHANGED: t("selectFilter.measures_validity_changed"),
    CATEGORY_CHANGED: t("selectFilter.category_changed"),
    CATEGORY_CREATED: t("selectFilter.category_created"),
    CATEGORY_DELETED: t("selectFilter.category_deleted"),
    CROSS_FILTERS_IMPORTED: t("selectFilter.cross_filters_imported"),
    CROSS_FILTERS_DELETED: t("selectFilter.cross_filters_deleted"),
    PROJECT_CATEGORY_CHANGED: t("selectFilter.project_category_changed"),
    PROJECT_CATEGORY_CREATED: t("selectFilter.project_category_created"),
    PROJECT_CATEGORY_DELETED: t("selectFilter.project_category_deleted"),
    COMMENT_CHANGED: t("selectFilter.comment_changed"),
    COMMENT_CREATED: t("selectFilter.comment_created"),
    COMMENT_DELETED: t("selectFilter.comment_deleted"),
    DIAGNOSIS_ENDED: t("selectFilter.diagnosis_ended"),
    FAILURE_REPARABILITY_CHANGED: t("selectFilter.failure.reparability.changed"),
    FAILURE_STATUS_CHANGED: t("selectFilter.failures_status_changed"),
    FORMULA_QUANTITY_CREATED: t("selectFilter.formula_quantity_created"),
    LIVE_DATA_FLOW_MANAGEMENT: t("selectFilter.live_data_flow_management"),
    PROJECT_CHANGED: t("selectFilter.project_changed"),
    PROJECT_CREATED: t("selectFilter.project_created"),
    PROJECT_DELETED: t("selectFilter.project_deleted"),
    PROJECT_DUPLICATED: t("selectFilter.project_duplicated"),
    PROJECT_DUPLICATED_FROM: t("selectFilter.project_duplicated_from"),
    SOURCE_DUPLICATED: t("selectFilter.source_duplicated"),
    PROJECT_MANUAL_FAILURE_METADATA_CHANGED: t(
      "activity.selectFilter.project_manual_failure_metadata.changed",
    ),
    QOIS_CHANGED: t("selectFilter.qois_changed"),
    QUANTITIES_DELETED: t("selectFilter.quantities_deleted"),
    QUANTITIES_CHANGED: t("selectFilter.quantities_changed"),
    QUANTITIES_REPLACED: t("selectFilter.quantities_replaced"),
    SETUP_CREATED: t("selectFilter.setup_created"),
    SETUP_CHANGED: t("selectFilter.setup_changed"),
    SETUP_DELETED: t("selectFilter.setup_deleted"),
    SETUP_DIAGNOSIS_PRESET_CHANGED: t("selectFilter.setup_diagnosis_preset_changed"),
    SETUP_DIAGNOSIS_PARAMETERS_CHANGED: t("selectFilter.setup_diagnosis_parameters_changed"),
    SETUP_DIAGNOSIS_FAILURES_CHANGED: t("selectFilter.setup_diagnosis_diagnosis_failures_changed"),
    SETUP_DIAGNOSIS_FAILURES_DELETED: t("selectFilter.setup_diagnosis_diagnosis_failures_deleted"),
    SETUP_DIAGNOSIS_FAILURES_METADATA_CHANGED: t(
      "selectFilter.setup_diagnosis_failures_metadata_changed",
    ),
    SETUP_DIAGNOSIS_FAILURES_REPLACED: t("selectFilter.setup_diagnosis_failures_replaced"),
    SETUP_DIAGNOSIS_OBSERVABLES_CHANGED: t("selectFilter.setup_diagnosis_observable_changed"),
    SETUP_DIAGNOSIS_OBSERVABLES_DELETED: t("selectFilter.setup_diagnosis_observable_deleted"),
    SETUP_DIAGNOSIS_OBSERVABLES_REPLACED: t("selectFilter.setup_diagnosis_observable_replaced"),
    SETUP_SIMULATION_INPUTS_DELETED: t("selectFilter.setup_simulation_input_deleted"),
    SETUP_SIMULATION_INPUTS_REPLACED: t("selectFilter.setup_simulation_input_replaced"),
    SETUP_SIMULATION_MODEL_DELETED: t("selectFilter.setup_simulation_model_deleted"),
    SETUP_SIMULATION_OUTPUTS_DELETED: t("selectFilter.setup_simulation_output_deleted"),
    SETUP_SIMULATION_OUTPUTS_REPLACED: t("selectFilter.setup_simulation_output_replaced"),
    SETUP_SIMULATION_MODEL_REPLACED: t("selectFilter.setup_simulation_model_replaced"),
    SETUP_SIMULATION_MODEL_STATUS_CHECKED: t("selectFilter.setup_simulation_model_status_checked"),
    SETUP_SIMULATION_OFFSET_CHANGED: t("selectFilter.setup_simulation_offset_changed"),
    SETUP_DUPLICATED: t("selectFilter.setup_duplicated"),
    SIMULATION_ENDED: t("selectFilter.simulation_ended"),
    SNAPSHOT_UPSERTED: t("selectFilter.snapshot_upserted"),
    SNAPSHOTS_DELETED: t("selectFilter.snapshots_deleted"),
    SENSOR_VALUE_DELETED: t("selectFilter.sensor_value_deleted"),
    SOURCE_DELETED: t("selectFilter.source.deleted"),
    SENSOR_QUANTITIES_CREATED: t("selectFilter.sensor_quantities_created"),
    OUTDATED: null,
    ANALYSIS_MANUAL_FAILURE_CHANGED: t("activity.selectFilter.analysis.manual_failure.changed"),
    PROJECT_MANUAL_FAILURE_CREATED: t("activity.selectFilter.project_manual_failure.created"),
    PROJECT_MANUAL_FAILURE_DELETED: t("activity.selectFilter.project_manual_failure.deleted"),
    FAILURE_REINTEGRATED: t("activity.selectFilter.failure_reintegrated"),
    DASHBOARD_CREATED: t("selectFilter.dashboard_created", translation),
    DASHBOARD_CHANGED: t("selectFilter.dashboard_changed", translation),
    DASHBOARD_PUBLICATION_CHANGED: t("selectFilter.dashboard_publication_changed", translation),
    DASHBOARD_DELETED: t("selectFilter.dashboard_deleted", translation),
    DASHBOARD_DUPLICATED: t("selectFilter.dashboard_duplicated", translation),
    FAULT_EVENT_STATUS_CHANGED: t("selectFilter.fault_event_status_changed"),
    FAULT_EVENT_NAME_CHANGED: t("selectFilter.fault_event_name_changed"),
    FAULT_EVENT_PRIORITY_CHANGED: t("selectFilter.fault_event_priority_changed"),
    FAULT_EVENT_DIAGNOSES_STATUS_CHANGED: t("selectFilter.fault_event_diagnoses_status_changed"),
    FAULT_EVENT_DASHBOARD_CREATED: t("selectFilter.fault_event_dashboard_created", translation),
    FAULT_EVENT_DASHBOARD_DELETED: t("selectFilter.fault_event_dashboard_deleted", translation),
    FAULT_EVENT_SPLIT: t("selectFilter.fault_event_split"),
    FAULT_EVENT_MERGE: t("selectFilter.fault_event_merge"),
    MANUAL_FAULT_EVENT_CREATED: t("activity.selectFilter.manual_fault_event.created"),
    MANUAL_FAULT_EVENT_STATUS_UPDATED: t("activity.selectFilter.manual_fault_event.status_updated"),
    MANUAL_FAULT_EVENT_DELETED: t("activity.selectFilter.manual_fault_event.deleted"),
    MANUAL_FAULT_EVENT_DASHBOARD_CREATED: t(
      "activity.selectFilter.manual_fault_event.dashboard_created",
    ),
    MANUAL_FAULT_EVENT_DASHBOARD_DELETED: t(
      "activity.selectFilter.manual_fault_event.dashboard_deleted",
    ),
    MANUAL_FAULT_EVENT_TIME_WINDOW_IMPACT_CREATED: t(
      "activity.selectFilter.manual_fault_event.time_window_created",
    ),
    MANUAL_FAULT_EVENT_TIME_WINDOW_IMPACT_DELETED: t(
      "activity.selectFilter.manual_fault_event.time_window_deleted",
    ),
    MANUAL_FAULT_EVENT_TIME_WINDOW_IMPACT_UPDATED: t(
      "activity.selectFilter.manual_fault_event.time_window_updated",
    ),
    DASHBOARD_COMMENT_CREATED: t("selectFilter.dashboard_comment_created", translation),
  } satisfies { [key in ActivityType]: string | null };

  switch (activity.type) {
    case "ANALYSIS_ACTIVATED":
      return {
        projectId: activity.projectId,
        title: t("analysis_activated.title", {
          authorName: <AuthorName activity={activity} />,
          nbAnalysis: activity.properties.analyses.length,
        }),
        details: () => (
          <AnalysisActivatedDetails
            projectId={activity.projectId}
            sortedAnalysisActivated={[...activity.properties.analyses].sort(comparatorBy("date"))}
          />
        ),
      };
    case "ANALYSIS_DEACTIVATED": {
      const sortedAnalysisDeactivated = [...activity.properties.analyses].sort(
        comparatorBy("date"),
      );
      return {
        projectId: activity.projectId,
        title: t("analysis_deactivated.title", {
          authorName: <AuthorName activity={activity} />,
          nbAnalysis: activity.properties.analyses.length,
        }),
        details: () => (
          <AnalysisDeactivated
            projectId={activity.projectId}
            sortedAnalysisDeactivated={sortedAnalysisDeactivated}
          />
        ),
      };
    }
    case "ANALYSIS_MEASURES_VALIDITY_CHANGED": {
      const sortedAnalyses = [...activity.properties.analyses].sort(comparatorBy("date"));
      return {
        projectId: activity.projectId,
        title: (
          <MesuresValidityChangedTitle
            authorName={<AuthorName activity={activity} />}
            action={activity.properties.action}
            projectId={activity.projectId}
            sortedAnalyses={sortedAnalyses}
          />
        ),
        details: () => (
          <AnalysisMeasuresValidityChangedDetails
            projectId={activity.projectId}
            sortedAnalysisMeasuresValidityChangedDetails={sortedAnalyses}
          />
        ),
      };
    }
    case "ANALYSIS_ACTIVATION_FILTERS_DELETED":
      return {
        projectId: activity.projectId,
        title: t("analysis_activation_filters_deleted.title", {
          authorName: <AuthorName activity={activity} />,
        }),
      };
    case "ANALYSIS_ACTIVATION_FILTERS_REPLACED":
      return {
        projectId: activity.projectId,
        title: t("analysis_activation_filters_replaced.title", {
          authorName: <AuthorName activity={activity} />,
        }),
      };
    case "ANALYSIS_ACTIVATION_RESET":
      return {
        projectId: activity.projectId,
        title: t("analysis_activation_reset.title", {
          authorName: <AuthorName activity={activity} />,
        }),
      };
    case "ANALYSIS_ACTIVATION_FREQUENCY_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("analysis_activation_frequency_changed.title", {
          authorName: <AuthorName activity={activity} />,
          frequency: activity.properties.frequency,
        }),
      };

    case "COMMENT_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("comment_changed.title", {
          authorName: <AuthorName activity={activity} />,
        }),
        details: () => (
          <CommentEditedDetails
            activityData={{
              projectId: activity.projectId,
              analysis: activity.analysis,
              currentCommentText: activity.properties.currentCommentText,
              previousCommentText: activity.properties.previousCommentText,
              faultEvent:
                activity.faultEventProjectId !== null &&
                activity.faultEventFailureName !== null &&
                activity.faultEventStartsAt !== null
                  ? {
                      projectId: activity.faultEventProjectId,
                      failureNames: activity.faultEventFailureName.split(", "),
                      start: activity.faultEventStartsAt,
                    }
                  : null,
            }}
          />
        ),
      };
    case "COMMENT_CREATED":
      return {
        projectId: activity.projectId,
        title: t("comment_created.title", {
          authorName: <AuthorName activity={activity} />,
        }),
        details: () => (
          <CommentCreatedDetails
            activityData={{
              projectId: activity.projectId,
              analysis: activity.analysis,
              commentText: activity.properties.commentText,
              dashboardName: activity.dashboardName,
              faultEvent:
                activity.faultEventId !== null ||
                (activity.faultEventProjectId !== null &&
                  activity.faultEventFailureName !== null &&
                  activity.faultEventStartsAt !== null)
                  ? {
                      id: activity.faultEventId,
                      projectId: activity.faultEventProjectId,
                      failureName: activity.faultEventFailureName,
                      start: activity.faultEventStartsAt,
                    }
                  : null,
            }}
          />
        ),
      };
    case "DASHBOARD_COMMENT_CREATED":
      return {
        projectId: activity.projectId,
        title: t(
          "comment_dashboard_created.title",
          {
            authorName: <AuthorName activity={activity} />,
            dashboardLink: DashboardLink(
              activity.projectId,
              activity.dashboardId,
              activity.dashboardName,
            ),
          },
          translation,
        ),
        // details: () => (
        //   <DashboardCommentCreatedDetails activityData={{ properties: activity.properties }} />
        // ),
      };
    case "CROSS_FILTERS_DELETED":
      return {
        projectId: activity.projectId,
        title: t("activity.description.cross_filters_deleted.title", {
          authorName: <AuthorName activity={activity} />,
        }),
      };
    case "CROSS_FILTERS_IMPORTED":
      return {
        projectId: activity.projectId,
        title: t("activity.description.cross_filters_imported.title", {
          authorName: <AuthorName activity={activity} />,
        }),
      };
    case "DIAGNOSIS_ENDED":
      return {
        projectId: activity.projectId,
        title: t("diagnosis_ended.title", {
          analysesLink: activity.projectId ? (
            <StyledLink
              to={toPath("/projects/:projectId/diagnosis", {
                projectId: activity.projectId,
              })}
            >
              {activity.properties.analyses.length}
            </StyledLink>
          ) : (
            <LinkDisabled>{activity.properties.analyses.length}</LinkDisabled>
          ),
          analysesCount: activity.properties.analyses.length,
        }),
        details: () => (
          <AnalysisList analyses={activity.properties.analyses} projectId={activity.projectId} />
        ),
      };
    case "COMMENT_DELETED":
      return {
        projectId: activity.projectId,
        title: t("comment_deleted.title", {
          authorName: <AuthorName activity={activity} />,
        }),
      };
    case "LIVE_DATA_FLOW_MANAGEMENT":
      return {
        projectId: activity.projectId,
        title: t("activity.description.live_data_flow_management.title", {
          authorName: <AuthorName activity={activity} />,
          active: activity.properties.active,
        }),
      };
    case "PROJECT_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("project_changed.title", {
          authorName: <AuthorName activity={activity} />,
        }),
        details: () => detailsProjectChanged(activity.properties, translation),
      };
    case "PROJECT_CREATED":
      return {
        projectId: activity.projectId,
        title: t("project_created.title", {
          authorName: <AuthorName activity={activity} />,
        }),
      };
    case "PROJECT_DELETED":
      return {
        projectId: activity.projectId,
        title: t("project_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          projectName: activity.projectName,
        }),
      };
    case "PROJECT_MANUAL_FAILURE_CREATED":
      return {
        projectId: activity.projectId,
        title: t("activity.project_manual_failure_created.title", {
          authorName: <AuthorName activity={activity} />,
          failureName: activity.properties.name,
        }),
      };
    case "PROJECT_MANUAL_FAILURE_METADATA_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("project_manual_failure_metadata_changed.title", {
          authorName: <AuthorName activity={activity} />,
          failureName: activity.properties.name,
        }),
        details() {
          return (
            <>
              <div key={activity.properties.name}>
                {detailsSetupDiagnosisFailuresMetadataChanged(activity.properties, translation)}
              </div>
            </>
          );
        },
      };
    case "PROJECT_DUPLICATED":
      return {
        projectId: activity.projectId,
        title: (
          <ProjectDuplicatedTitle
            authorName={<AuthorName activity={activity} />}
            targetProject={{
              id: activity.properties.targetProject.id,
              name: activity.properties.targetProject.name,
            }}
          />
        ),
      };
    case "PROJECT_DUPLICATED_FROM":
      return {
        projectId: activity.projectId,
        title: (
          <ProjectDuplicatedFromTitle
            authorName={<AuthorName activity={activity} />}
            sourceProjet={{
              id: activity.properties.sourceProject.id,
              name: activity.properties.sourceProject.name,
            }}
          />
        ),
      };
    case "SOURCE_DUPLICATED":
      return {
        title: (
          <>
            {t("activity.source_duplicated.title", {
              authorName: <AuthorName activity={activity} />,
              originSourceId: activity.properties.originSourceId,
              targetSourceId: activity.properties.targetSourceId,
            })}
            {getSourceDuplicationRangeLabel(activity.properties.from, activity.properties.to)}
          </>
        ),
      };
    case "SETUP_DUPLICATED":
      return {
        projectId: activity.projectId,
        title: t("setup_duplicated.title", {
          authorName: <AuthorName activity={activity} />,
          sourceSetupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <LinkDisabled>{activity.setupName}</LinkDisabled>
            ),
          targetSetupLink: (
            <SetupDuplicatedTitle
              projectId={activity.projectId}
              setupId={activity.properties.targetSetup.id}
              setupName={activity.properties.targetSetup.name}
            />
          ),
        }),
      };
    case "PROJECT_MANUAL_FAILURE_DELETED":
      return {
        projectId: activity.projectId,
        title: t("activity.project_manual_failure_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          failureName: activity.properties.name,
        }),
      };
    case "SETUP_DELETED":
      return {
        projectId: activity.projectId,
        title: t("setup_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          setupName: activity.setupName,
        }),
      };
    case "SETUP_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("setup_changed.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "FAILURE_STATUS_CHANGED": {
      const sortedAnalyses = [...activity.properties.analyses].sort(comparatorBy("date"));
      return {
        projectId: activity.projectId,
        title: (
          <FailureStatusChangedTitle
            authorName={<AuthorName activity={activity} />}
            status={activity.properties.status}
            projectId={activity.projectId}
            sortedAnalyses={sortedAnalyses}
          />
        ),
        details: () => (
          <FailureStatusChangedDetails
            sortedFailureStatusChanged={sortedAnalyses}
            projectId={activity.projectId}
          />
        ),
      };
    }
    case "FAILURE_REINTEGRATED": {
      const sortedAnalyses = [...activity.properties.analyses].sort(comparatorBy("date"));
      return {
        projectId: activity.projectId,
        title: (
          <FailureReintegratedTitle
            authorName={<AuthorName activity={activity} />}
            projectId={activity.projectId}
            sortedAnalyses={sortedAnalyses}
          />
        ),
      };
    }
    case "FAILURE_REPARABILITY_CHANGED": {
      const sortedAnalyses = [...activity.properties.analyses].sort(comparatorBy("date"));

      return {
        projectId: activity.projectId,
        title: (
          <FailureReparabilityChangedTitle
            authorName={<AuthorName activity={activity} />}
            reparability={activity.properties.reparability}
            projectId={activity.projectId}
            sortedAnalyses={sortedAnalyses}
            translation={translation}
          />
        ),
        details: () => (
          <FailureReparabilityChangedDetails
            sortedFailureReparabilityChanged={sortedAnalyses}
            projectId={activity.projectId}
          />
        ),
      };
    }
    case "FORMULA_QUANTITY_CREATED":
      return {
        projectId: activity.projectId,
        title: t("formula_quantity_created.title", {
          authorName: <AuthorName activity={activity} />,
          quantityName: activity.properties.quantityName,
          formula: activity.properties.formula,
        }),
      };
    case "SETUP_CREATED":
      return {
        projectId: activity.projectId,
        title: t("setup_created.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_DIAGNOSIS_PRESET_CHANGED":
      return {
        projectId: activity.projectId,
        title: <SetupPresetChangedTitle activity={activity} />,
      };
    case "SETUP_DIAGNOSIS_PARAMETERS_CHANGED":
      return {
        projectId: activity.projectId,
        title: <SetupParametersChangedTitle activity={activity} />,
        details: () => <SetupParametersChangedDetails properties={activity.properties} />,
      };
    case "QUANTITIES_CHANGED": {
      return {
        projectId: activity.projectId,
        title: t("quantities_changed.title", {
          authorName: <AuthorName activity={activity} />,
          nbQuantities: activity.properties.length,
          quantitiesLink: activity?.projectId ? (
            <StyledLink
              to={toPath("/projects/:projectId/settings/quantities", {
                projectId: activity.projectId,
              })}
            >
              {t("lexical.quantities")}
            </StyledLink>
          ) : (
            <LinkDisabled>{t("lexical.quantities")}</LinkDisabled>
          ),
        }),
        details() {
          return (
            <>
              {activity.properties.sort(comparatorBy("name")).map(property => (
                <div key={property.name}>
                  <strong>{property.name}</strong>
                  {detailsQuantitiesChanged(property, projectTimeZone)}
                </div>
              ))}
            </>
          );
        },
      };
    }
    case "QUANTITIES_REPLACED":
      return {
        projectId: activity.projectId,
        title: t("quantities_replaced.title", {
          authorName: <AuthorName activity={activity} />,
        }),
      };
    case "QUANTITIES_DELETED":
      return {
        projectId: activity.projectId,
        title: t("quantities_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          count: activity.properties.quantityNames.length,
        }),
        details: () => (
          <List>
            {activity.properties.quantityNames.map(name => (
              <ul key={`${name}`}>
                <SublistItem>{name}</SublistItem>
              </ul>
            ))}
          </List>
        ),
      };
    case "QOIS_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("qois_changed.title", {
          authorName: <AuthorName activity={activity} />,
          qoiNames: activity.properties.quantityNames,
        }),
      };
    case "SENSOR_QUANTITIES_CREATED":
      return {
        projectId: activity.projectId,
        title: t("sensor_quantities_created.title", {
          authorName: <AuthorName activity={activity} />,
          count: activity.properties.sensorNames.length,
        }),
        details: () => (
          <List>
            {activity.properties.sensorNames.map(name => (
              <ul key={`${name}`}>
                <SublistItem>{name}</SublistItem>
              </ul>
            ))}
          </List>
        ),
      };
    case "SETUP_DIAGNOSIS_OBSERVABLES_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("setup_observable_changed.title", {
          authorName: <AuthorName activity={activity} />,
          properties: activity.properties,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/diagnosis", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_DIAGNOSIS_OBSERVABLES_DELETED":
      return {
        projectId: activity.projectId,
        title: t("setup_observable_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/diagnosis", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_DIAGNOSIS_OBSERVABLES_REPLACED":
      return {
        projectId: activity.projectId,
        title: t("setup_observable_replaced.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/diagnosis", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_DIAGNOSIS_FAILURES_REPLACED":
      return {
        projectId: activity.projectId,
        title: t("setup_diagnosis_failures_replaced.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/diagnosis", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_DIAGNOSIS_FAILURES_DELETED":
      return {
        projectId: activity.projectId,
        title: t("setup_diagnosis_failures_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/diagnosis", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_SIMULATION_MODEL_STATUS_CHECKED":
      return {
        projectId: activity.projectId,
        title: t("setup_simulation_model_status_checked.title", {
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/simulation", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
          validated: activity.properties.valid,
        }),
      };
    case "SETUP_SIMULATION_MODEL_REPLACED":
      return {
        projectId: activity.projectId,
        title: t("setup_simulation_model_replaced.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/simulation", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_SIMULATION_MODEL_DELETED":
      return {
        projectId: activity.projectId,
        title: t("setup_simulation_model_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/simulation", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_DIAGNOSIS_FAILURES_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("setup_failures_changed.title", {
          authorName: <AuthorName activity={activity} />,
          active: activity.properties.active,
          name: activity.properties.name,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/diagnosis", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_DIAGNOSIS_FAILURES_METADATA_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("setup_failures_metadata_changed.title", {
          authorName: <AuthorName activity={activity} />,
          name: activity.properties.name,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/diagnosis", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
        details() {
          return (
            <>
              <div key={activity.properties.name}>
                {detailsSetupDiagnosisFailuresMetadataChanged(activity.properties, translation)}
              </div>
            </>
          );
        },
      };
    case "SETUP_SIMULATION_INPUTS_DELETED":
      return {
        projectId: activity.projectId,
        title: t("setup_simulation_inputs.deleted.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/simulation", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_SIMULATION_INPUTS_REPLACED":
      return {
        projectId: activity.projectId,
        title: t("setup_simulation_inputs.replaced.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/simulation", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_SIMULATION_OUTPUTS_DELETED":
      return {
        projectId: activity.projectId,
        title: t("setup_simulation_outputs.deleted.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/simulation", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_SIMULATION_OUTPUTS_REPLACED":
      return {
        projectId: activity.projectId,
        title: t("setup_simulation_outputs.replaced.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/simulation", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
        }),
      };
    case "SETUP_SIMULATION_OFFSET_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("setup_simulation_offset_changed.title", {
          authorName: <AuthorName activity={activity} />,
          setupLink:
            activity.projectId && activity.setupId ? (
              <>
                {t("activity.badge.setup.setting")}{" "}
                <StyledLink
                  to={toPath("/projects/:projectId/setup/:setupId/simulation", {
                    projectId: activity.projectId,
                    setupId: activity.setupId,
                  })}
                >
                  {activity.setupName}
                </StyledLink>
              </>
            ) : (
              <BadgeDeletedSetup setupName={activity.setupName} />
            ),
          properties: activity.properties,
        }),
      };
    case "SIMULATION_ENDED":
      return {
        projectId: activity.projectId,
        title: t("simulation_ended.title", {
          analysesLink: activity.projectId ? (
            <StyledLink
              to={toPath("/projects/:projectId/symptoms", {
                projectId: activity.projectId,
              })}
            >
              {activity.properties.analyses.length}
            </StyledLink>
          ) : (
            <LinkDisabled>{activity.properties.analyses.length}</LinkDisabled>
          ),
          analysesCount: activity.properties.analyses.length,
        }),
        details: () => (
          <AnalysisList analyses={activity.properties.analyses} projectId={activity.projectId} />
        ),
      };
    case "SNAPSHOT_UPSERTED":
      return {
        title: t("source_upserted.title", {
          authorName: <AuthorName activity={activity} />,
          nbPoints:
            activity.properties.conflictingSnapshotsDiffCount +
            activity.properties.mergedSnapshotsCount +
            activity.properties.ignoredSnapshotsCount +
            activity.properties.newSnapshotsCount,
          sourceLink: activity.sourceId ? (
            <StyledLink to={toPath("/sources", {})}>{activity.properties.sourceId}</StyledLink>
          ) : (
            <LinkDisabled>{activity.properties.sourceId}</LinkDisabled>
          ),
          isSourceCreated: activity.properties.sourceCreated,
        }),
        details: () => <PatchSnapshotsSummary patchResponse={activity.properties} />,
      };
    case "SNAPSHOTS_DELETED":
      return {
        title: t("snapshots_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          snapshotCount: activity.properties.snapshotCount,
          sourceLink: activity.sourceId ? (
            <StyledLink to={toPath("/sources", {})}>{activity.properties.sourceId}</StyledLink>
          ) : (
            <LinkDisabled>{activity.properties.sourceId}</LinkDisabled>
          ),
        }),
        details: () => <SnapshotList snapshots={activity.properties.snapshots} />,
      };
    case "SENSOR_VALUE_DELETED":
      return {
        title: t("sensor_value_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          sensorTag: activity.properties.sensorTag,
          startPeriod: formatDate(activity.properties.startPeriod, "date", null),
          endPeriod: formatDate(activity.properties.endPeriod, "date", null),
          sourceLink: activity.sourceId ? (
            <StyledLink to={toPath("/sources", {})}>{activity.properties.sourceId}</StyledLink>
          ) : (
            <LinkDisabled>{activity.properties.sourceId}</LinkDisabled>
          ),
        }),
      };
    case "SOURCE_DELETED":
      return {
        title: t("source_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          sourceName: activity.properties.sourceName,
        }),
      };
    case "CATEGORY_CREATED":
      return {
        title: t("category_created.title", {
          authorName: <AuthorName activity={activity} />,
          categoryName: activity.properties.categoryName,
          type: activity.properties.type,
          translation,
        }),
      };
    case "DASHBOARD_CREATED":
      return {
        projectId: activity.projectId,
        title: t(
          "dashboard_created.title",
          {
            authorName: <AuthorName activity={activity} />,
            dashboardLink: DashboardLink(
              activity.projectId,
              activity.dashboardId,
              activity.dashboardName,
            ),
          },
          translation,
        ),
      };
    case "DASHBOARD_CHANGED":
      return {
        projectId: activity.projectId,
        title: t(
          "dashboard_changed.title",
          {
            authorName: <AuthorName activity={activity} />,
            dashboardLink: DashboardLink(
              activity.projectId,
              activity.dashboardId,
              activity.dashboardName,
            ),
          },
          translation,
        ),
      };
    case "DASHBOARD_DELETED":
      return {
        projectId: activity.projectId,
        title: t(
          "dashboard_deleted.title",
          {
            authorName: <AuthorName activity={activity} />,
            dashboardName: activity.properties.name,
          },
          translation,
        ),
      };
    case "DASHBOARD_PUBLICATION_CHANGED":
      return {
        projectId: activity.projectId,
        title: t(
          "dashboard_publication_changed.title",
          {
            authorName: <AuthorName activity={activity} />,
            dashboardLink: DashboardLink(
              activity.projectId,
              activity.dashboardId,
              activity.dashboardName,
            ),
            published: activity.properties.published,
            readonly: activity.properties.readonly,
          },
          translation,
        ),
      };
    case "DASHBOARD_DUPLICATED":
      return {
        projectId: activity.projectId,
        title: t(
          "dashboard_duplicated.title",
          {
            authorName: <AuthorName activity={activity} />,
            sourceDashboardLink: DashboardLink(
              activity.projectId,
              activity.dashboardId,
              activity.dashboardName,
            ),
            targetDashboardLink: DashboardLink(
              activity.projectId,
              activity.properties.targetDashboard.id,
              activity.properties.targetDashboard.name,
            ),
          },
          translation,
        ),
      };
    case "CATEGORY_DELETED":
      return {
        title: t("category_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          categoryName: activity.properties.categoryName,
          type: activity.properties.type,
          translation,
        }),
      };
    case "CATEGORY_CHANGED":
      return {
        title: t("category_changed.title", {
          authorName: <AuthorName activity={activity} />,
          previousName: activity.properties.previousCategoryName,
          currentName: activity.properties.currentCategoryName,
          type: activity.properties.type,
          translation,
        }),
      };
    case "PROJECT_CATEGORY_CREATED":
      return {
        projectId: activity.projectId,
        title: t("project_category_created.title", {
          authorName: <AuthorName activity={activity} />,
          categoryName: activity.properties.categoryName,
          type: activity.properties.type,
        }),
      };
    case "PROJECT_CATEGORY_DELETED":
      return {
        projectId: activity.projectId,
        title: t("project_category_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          categoryName: activity.properties.categoryName,
          type: activity.properties.type,
        }),
      };
    case "PROJECT_CATEGORY_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("project_category_changed.title", {
          authorName: <AuthorName activity={activity} />,
          previousName: activity.properties.previousCategoryName,
          currentName: activity.properties.currentCategoryName,
          type: activity.properties.type,
        }),
      };
    case "ANALYSIS_PUBLICATION_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("analysis_publication_changed.title", {
          authorName: <AuthorName activity={activity} />,
          publishedCount: activity.properties.analysis.published.length,
          unpublishedCount: activity.properties.analysis.unpublished.length,
        }),
        details: () => (
          <List>
            {activity.properties.analysis.published.length > 0 && (
              <AnalysisList
                title={t("analysis.publish.title")}
                projectId={activity.projectId}
                analyses={activity.properties.analysis.published.map(a => ({
                  id: a.id,
                  date: a.date,
                  group: a.group,
                  label: a.label,
                }))}
              />
            )}
            {activity.properties.analysis.unpublished.length > 0 && (
              <AnalysisList
                title={t("analysis.unpublish.title")}
                projectId={activity.projectId}
                analyses={activity.properties.analysis.unpublished.map(a => ({
                  id: a.id,
                  date: a.date,
                  group: a.group,
                  label: a.label,
                }))}
              />
            )}
          </List>
        ),
      };
    case "ANALYSIS_MANUAL_FAILURE_CHANGED": {
      type AnalysesManualFailureEdited = (typeof activity.properties.changes)[0]["edited"][0];
      const changes = activity.properties.changes;
      const addedAnalyses = changes.map(change => change.added).flat();
      const deletedAnalyses = changes.map(change => change.deleted).flat();
      const editedAnalyses = changes
        .map(change => {
          const { edited } = change;
          const editedImpactChanges = edited.reduce<AnalysesManualFailureEdited[]>((acc, e) => {
            if (
              e.diff.impacts.some(impact => impact.current !== impact.previous) ||
              e.diff.reparability?.previous !== e.diff.reparability?.current
            ) {
              return [...acc, e];
            } else {
              return acc;
            }
          }, []);
          return editedImpactChanges;
        })
        .flat()
        .map(a => a.analyses)
        .flat();

      const globalUniqueImpactedAnalysesCount = [
        ...new Map(
          [...addedAnalyses, ...deletedAnalyses, ...editedAnalyses].map(v => [v.id, v]),
        ).values(),
      ].length;

      return {
        projectId: activity.projectId,
        title: t("activity.analysis.manual_failure_changed.title", {
          authorName: <AuthorName activity={activity} />,
          globalImpactedAnalysesCount: globalUniqueImpactedAnalysesCount,
          failureLink: activity.projectId ? (
            <StyledLink
              to={toPath("/projects/:projectId/diagnosis", {
                projectId: activity.projectId,
              })}
            >
              {t("activity.manual_failure", { count: changes.length })}
            </StyledLink>
          ) : (
            <LinkDisabled>{activity.properties.changes.length}</LinkDisabled>
          ),
        }),
        details() {
          return (
            <List>
              {activity.properties.changes.map((change, index) => {
                const { failureName, added, edited, deleted } = change;
                const editedImpactChanges = edited.reduce<AnalysesManualFailureEdited[]>(
                  (acc, e) => {
                    if (e.diff.impacts.some(impact => impact.current !== impact.previous)) {
                      return [...acc, e];
                    } else {
                      return acc;
                    }
                  },
                  [],
                );
                const editedReparabilityChanges = edited.reduce<AnalysesManualFailureEdited[]>(
                  (acc, e) => {
                    if (e.diff.reparability?.current === e.diff.reparability?.previous) {
                      return acc;
                    } else {
                      return [...acc, e];
                    }
                  },
                  [],
                );

                const keyFailureAdded = `${failureName}-added-${index}`;
                const keyFailureDeleted = `${failureName}-deleted-${index}`;
                const keyFailureEdited = `${failureName}-edited-${index}`;

                return (
                  <div key={keyFailureAdded}>
                    <ListItemTitle>{failureName}</ListItemTitle>
                    {added.length > 0 && (
                      <>
                        <SublistItem>
                          {t("activity.analysis.manual_failure_changed.analyses_added.subtitle", {
                            count: added.length,
                          })}
                        </SublistItem>
                        <SublistItem>
                          <AnalysisList projectId={activity.projectId} analyses={added} />
                        </SublistItem>
                      </>
                    )}
                    {edited.length > 0 && (
                      <div key={keyFailureEdited}>
                        {editedReparabilityChanges.map(editedReaparabilityChange => {
                          const key = `editedReaparabilityChange-${index}`;
                          return (
                            <div key={key}>
                              <SublistItem>
                                {t(
                                  "activity.analysis.manual_failure_changed.analyses_edited_reparability.subtitle",
                                  {
                                    count: editedReaparabilityChange.analyses.length,
                                    reparability: editedReaparabilityChange.diff.reparability,
                                    translation,
                                  },
                                )}
                              </SublistItem>
                              <SublistItem>
                                <AnalysisList
                                  projectId={activity.projectId}
                                  analyses={editedReaparabilityChange.analyses}
                                />
                              </SublistItem>
                            </div>
                          );
                        })}
                        {editedImpactChanges.map(editedImpactChange => {
                          const key = `editedImpactChange-${index}`;
                          return (
                            <div key={key}>
                              <SublistItem>
                                {t(
                                  "activity.analysis.manual_failure_changed.analyses_edited_impacts.subtitle",
                                  {
                                    count: editedImpactChange.analyses.length,
                                    impacts: editedImpactChange.diff.impacts,
                                  },
                                )}
                              </SublistItem>
                              <SublistItem>
                                <AnalysisList
                                  projectId={activity.projectId}
                                  analyses={editedImpactChange.analyses}
                                />
                              </SublistItem>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {deleted.length > 0 && (
                      <div key={keyFailureDeleted}>
                        <SublistItem>
                          {t("activity.analysis.manual_failure_changed.analyses_deleted.subtitle", {
                            count: deleted.length,
                          })}
                        </SublistItem>
                        <SublistItem>
                          <AnalysisList projectId={activity.projectId} analyses={deleted} />
                        </SublistItem>
                      </div>
                    )}
                  </div>
                );
              })}
            </List>
          );
        },
      };
    }
    case "FAULT_EVENT_STATUS_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("fault_event_status_changed.title", {
          authorName: <AuthorName activity={activity} />,
          faultEventLink: FaultEventLink(
            activity.faultEventProjectId,
            activity.faultEventId,
            activity.faultEventName,
          ),
        }),
        details: () => detailsFaultEventStatusChanged(activity.properties, translation),
      };
    case "FAULT_EVENT_NAME_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("fault_event_name_changed.title", {
          authorName: <AuthorName activity={activity} />,
          properties: activity.properties,
          faultEventLink: FaultEventLink(
            activity.faultEventProjectId,
            activity.faultEventId,
            activity.faultEventName,
          ),
        }),
      };
    case "FAULT_EVENT_PRIORITY_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("fault_event_priority_changed.title", {
          authorName: <AuthorName activity={activity} />,
          faultEventLink: FaultEventLink(
            activity.faultEventProjectId,
            activity.faultEventId,
            activity.faultEventName,
          ),
        }),
        details() {
          return (
            <div>
              {t("fault_event_priority_changed.details", {
                previous: activity.properties.priority.previous,
                current: activity.properties.priority.current,
              })}
            </div>
          );
        },
      };
    case "FAULT_EVENT_DIAGNOSES_STATUS_CHANGED":
      return {
        projectId: activity.projectId,
        title: t("fault_event_diagnoses_status_changed.title", {
          authorName: <AuthorName activity={activity} />,
          properties: activity.properties,
          faultEventLink: FaultEventLink(
            activity.faultEventProjectId,
            activity.faultEventId,
            activity.faultEventName,
          ),
        }),
      };
    case "FAULT_EVENT_DASHBOARD_CREATED":
      return {
        projectId: activity.projectId,
        title: t(
          "fault_event_dashboard_created.title",
          {
            authorName: <AuthorName activity={activity} />,
            faultEventLink: FaultEventLink(
              activity.faultEventProjectId,
              activity.faultEventId,
              activity.faultEventName ?? "",
            ),
            dashboardLink: DashboardLink(
              activity.projectId,
              activity.dashboardId,
              activity.dashboardName,
            ),
          },
          translation,
        ),
      };
    case "FAULT_EVENT_DASHBOARD_DELETED":
      return {
        projectId: activity.projectId,
        title: t(
          "fault_event_dashboard_deleted.title",
          {
            authorName: <AuthorName activity={activity} />,
            faultEventLink: FaultEventLink(
              activity.faultEventProjectId,
              activity.faultEventId,
              activity.faultEventName ?? "",
            ),
            dashboardLink: DashboardLink(
              activity.projectId,
              activity.dashboardId,
              activity.dashboardName,
            ),
          },
          translation,
        ),
      };
    case "FAULT_EVENT_SPLIT":
      return {
        projectId: activity.projectId,
        title: t("fault_event_split.title", {
          authorName: <AuthorName activity={activity} />,
          originalEventLink: FaultEventLink(
            activity.faultEventProjectId,
            activity.faultEventId,
            activity.properties.newSourceEventName ?? "",
          ),
          newSplitEventLink: FaultEventLink(
            activity.faultEventProjectId,
            activity.properties.splitEventId ??
              convertFaultEventNaturalKeyToId(
                activity.projectId ?? "",
                activity.faultEventFailureName,
                new Date(activity.properties.splitEventStartAt),
              ),
            activity.properties.splitEventName ?? "",
          ),
        }),
      };
    case "FAULT_EVENT_MERGE":
      return {
        projectId: activity.projectId,
        title: t("fault_event_merge.title", {
          authorName: <AuthorName activity={activity} />,
          mergeEventLink: FaultEventLink(
            activity.faultEventProjectId,
            activity.faultEventId,
            activity.faultEventName ?? "",
          ),
          targetEventName: activity.properties.targetEventName ?? "",
          sourceEventName: activity.properties.sourceEventName ?? "",
        }),
      };
    case "OUTDATED":
      return {
        title: t("outdated.title", {
          action: activityLabels[activity.oldType as ActivityType] ?? activity.oldType,
          authorName: <AuthorName activity={activity} />,
        }),
        projectId: activity.projectId ?? undefined,
        details() {
          return <pre>{JSON.stringify(activity.properties, null, 2)}</pre>;
        },
      };
    case "MANUAL_FAULT_EVENT_CREATED":
      return {
        projectId: activity.projectId,
        title: t("activity.manual_fault_event_created.title", {
          authorName: <AuthorName activity={activity} />,
          eventLink: ManualFaultEventLink(
            activity.projectId,
            activity.manualFaultEventId,
            activity.manualFaultEventName ?? "",
          ),
        }),
      };
    case "MANUAL_FAULT_EVENT_DELETED":
      return {
        projectId: activity.projectId,
        title: t("activity.manual_fault_event_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          eventLink: ManualFaultEventLink(
            activity.projectId,
            activity.manualFaultEventId,
            activity.manualFaultEventName ?? "",
          ),
        }),
      };
    case "MANUAL_FAULT_EVENT_STATUS_UPDATED": {
      const formatTextValue = (value: string | null) => (value === null ? "-" : value);
      const formatDateValue = (value: string | null) =>
        value === null ? "-" : formatDate(value, "date", null);
      const formatPriorityValue = (value: PriorityFaultEvent | null) =>
        value === null ? "-" : getEventPriorityText(value);
      const formatReparabilityValue = (value: Reparability | null) =>
        value === null ? globalT("reparability.none") : globalT("reparability", value, translation);
      const getStatusLabel = (status: {
        identifiedAction: boolean | null;
        closedAt: string | null;
      }) => {
        const normalizedStatus: DenormalizedEventStatus = status.closedAt
          ? "CLOSED"
          : status.identifiedAction
            ? "ACTION_IDENTIFIED"
            : "ONGOING";
        return getEventStatusText(normalizedStatus);
      };
      const statusDetails = activity.properties.status;
      const details: { key: string; content: React.ReactNode }[] = [];

      const previousName = activity.properties.name.previous;
      const currentName = activity.properties.name.current;
      const previousDescription = activity.properties.description.previous;
      const currentDescription = activity.properties.description.current;

      if (previousName !== currentName) {
        details.push({
          key: "name",
          content: (
            <Text type="body2">
              {t("activity.manual_fault_event_status_updated.details.name", {
                previous: formatTextValue(previousName),
                current: formatTextValue(currentName),
              })}
            </Text>
          ),
        });
      }

      if (activity.properties.priority.previous !== activity.properties.priority.current) {
        details.push({
          key: "priority",
          content: (
            <Text type="body2">
              {t("activity.manual_fault_event_status_updated.details.priority", {
                previous: formatPriorityValue(activity.properties.priority.previous),
                current: formatPriorityValue(activity.properties.priority.current),
              })}
            </Text>
          ),
        });
      }

      if (previousDescription !== currentDescription) {
        details.push({
          key: "description",
          content: (
            <Text type="body2">
              {t("activity.manual_fault_event_status_updated.details.description", {
                previous: formatTextValue(previousDescription),
                current: formatTextValue(currentDescription),
              })}
            </Text>
          ),
        });
      }

      if (activity.properties.reparability.previous !== activity.properties.reparability.current) {
        details.push({
          key: "reparability",
          content: (
            <Text type="body2">
              {t("activity.manual_fault_event_status_updated.details.reparability", {
                previous: formatReparabilityValue(activity.properties.reparability.previous),
                current: formatReparabilityValue(activity.properties.reparability.current),
              })}
            </Text>
          ),
        });
      }

      const previousAction = activity.properties.identifiedAction.previous;
      const currentAction = activity.properties.identifiedAction.current;

      if (previousAction !== currentAction) {
        details.push({
          key: "identified-action",
          content: (
            <Text type="body2">
              {previousAction
                ? t("activity.manual_fault_event_status_updated.details.identified_action", {
                    previous: formatTextValue(previousAction),
                    current: formatTextValue(currentAction),
                  })
                : t("activity.manual_fault_event_status_updated.details.identified_action_set", {
                    current: formatTextValue(currentAction),
                  })}
            </Text>
          ),
        });
      }

      if (activity.properties.closedAt.previous !== activity.properties.closedAt.current) {
        details.push({
          key: "closed-at",
          content: (
            <Text type="body2">
              {t("activity.manual_fault_event_status_updated.details.closed_at", {
                previous: formatDateValue(activity.properties.closedAt.previous),
                current: formatDateValue(activity.properties.closedAt.current),
              })}
            </Text>
          ),
        });
      }

      if (
        statusDetails.previous.closedAt !== statusDetails.current.closedAt ||
        statusDetails.previous.identifiedAction !== statusDetails.current.identifiedAction
      ) {
        details.push({
          key: "status",
          content: (
            <Text type="body2">
              {t("activity.manual_fault_event_status_updated.details.status", {
                previous: getStatusLabel(statusDetails.previous),
                current: getStatusLabel(statusDetails.current),
              })}
            </Text>
          ),
        });
      }

      return {
        projectId: activity.projectId,
        title: t("activity.manual_fault_event_status_updated.title", {
          authorName: <AuthorName activity={activity} />,
          eventLink: ManualFaultEventLink(
            activity.projectId,
            activity.manualFaultEventId,
            activity.manualFaultEventName ?? "",
          ),
        }),
        details: details.length
          ? () => (
              <List>
                {details.map(detail => (
                  <li key={detail.key}>{detail.content}</li>
                ))}
              </List>
            )
          : undefined,
      };
    }
    case "MANUAL_FAULT_EVENT_DASHBOARD_CREATED":
      return {
        projectId: activity.projectId,
        title: t("activity.manual_fault_event_dashboard_created.title", {
          authorName: <AuthorName activity={activity} />,
          eventLink: ManualFaultEventLink(
            activity.projectId,
            activity.manualFaultEventId,
            activity.manualFaultEventName ?? "",
          ),
          dashboardLink: DashboardLink(
            activity.projectId,
            activity.dashboardId,
            activity.dashboardName ?? "",
          ),
        }),
      };
    case "MANUAL_FAULT_EVENT_DASHBOARD_DELETED":
      return {
        projectId: activity.projectId,
        title: t("activity.manual_fault_event_dashboard_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          eventLink: ManualFaultEventLink(
            activity.projectId,
            activity.manualFaultEventId,
            activity.manualFaultEventName ?? "",
          ),
          dashboardLink: DashboardLink(
            activity.projectId,
            activity.dashboardId,
            activity.dashboardName ?? "",
          ),
        }),
      };
    case "MANUAL_FAULT_EVENT_TIME_WINDOW_IMPACT_CREATED":
      return {
        projectId: activity.projectId,
        title: t("activity.manual_fault_event_time_window_created.title", {
          authorName: <AuthorName activity={activity} />,
          eventLink: ManualFaultEventLink(
            activity.projectId,
            activity.manualFaultEventId,
            activity.manualFaultEventName ?? "",
          ),
          startDate: formatDate(activity.properties.start, "date", null),
        }),
      };
    case "MANUAL_FAULT_EVENT_TIME_WINDOW_IMPACT_DELETED":
      return {
        projectId: activity.projectId,
        title: t("activity.manual_fault_event_time_window_deleted.title", {
          authorName: <AuthorName activity={activity} />,
          eventLink: ManualFaultEventLink(
            activity.projectId,
            activity.manualFaultEventId,
            activity.manualFaultEventName ?? "",
          ),
          startDate: formatDate(activity.properties.start, "date", null),
          endDate:
            activity.properties.closedAt === null
              ? null
              : formatDate(activity.properties.closedAt, "date", null),
        }),
      };
    case "MANUAL_FAULT_EVENT_TIME_WINDOW_IMPACT_UPDATED": {
      const timeWindows = activity.properties.timeWindows ?? [];
      const firstTimeWindow = timeWindows[0];

      return {
        projectId: activity.projectId,
        title: t("activity.manual_fault_event_time_window_updated.title", {
          authorName: <AuthorName activity={activity} />,
          eventLink: ManualFaultEventLink(
            activity.projectId,
            activity.manualFaultEventId,
            activity.manualFaultEventName ?? "",
          ),
          startDate: firstTimeWindow ? formatDate(firstTimeWindow.start, "date", null) : "",
          endDate:
            firstTimeWindow?.closedAt !== undefined && firstTimeWindow.closedAt !== null
              ? formatDate(firstTimeWindow.closedAt, "date", null)
              : null,
        }),
        details: () => (
          <List>
            {timeWindows.map(timeWindow => (
              <li key={`${timeWindow.start}-${timeWindow.closedAt ?? "open"}`}>
                <ListItemTitle>
                  {formatManualFaultEventTimeWindow(
                    formatDate(timeWindow.start, "date", null),
                    timeWindow.closedAt === null
                      ? null
                      : formatDate(timeWindow.closedAt, "date", null),
                  )}
                </ListItemTitle>
                <List>
                  <SublistItem>
                    {t("activity.manual_fault_event_time_window_updated.details.start", {
                      startDate: formatDate(timeWindow.start, "date", null),
                    })}
                  </SublistItem>
                  {timeWindow.impacts.map(impact => (
                    <SublistItem key={`${impact.quantityId}-${impact.name}`}>
                      {t("activity.manual_fault_event_time_window_updated.details.impact", {
                        indicatorName: impact.name,
                        value: impact.value,
                        unit: impact.unit,
                      })}
                    </SublistItem>
                  ))}
                </List>
              </li>
            ))}
          </List>
        ),
      };
    }
    default:
      throw new Error(activity satisfies never);
  }
}

export default getActivityDescription;
